import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageTitle, Badge } from "@/components/app/ui";
import { api, errMsg } from "@/lib/api";

const PLANS = [["enterprise", "Enterprise (Full Access)"], ["pro", "Pro"], ["growth", "Growth"], ["starter", "Starter"]];
const blank = { company_name: "", owner_name: "", owner_email: "", password: "", plan: "enterprise", payment_mode: "complimentary", months: 12, credits: "", notes: "", send_invite: true };

const Pick = ({ label, value, onChange, opts, testId }) => (
  <div className="space-y-2"><Label>{label}</Label><Select value={value} onValueChange={onChange}><SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>{opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
);

const CreateDialog = ({ open, setOpen, onCreated }) => {
  const [f, setF] = useState(blank);
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    try {
      const body = { ...f, credits: f.credits === "" ? null : Number(f.credits), months: Number(f.months), password: f.password || null };
      const { data } = await api.post("/admin/companies", body);
      setResult(data); onCreated(); toast.success("Company created");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const close = (o) => { if (!o) { setOpen(false); setResult(null); setF(blank); } };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{result ? "Company created" : "Add company / customer"}</DialogTitle></DialogHeader>
        {result ? (
          <div className="space-y-3 text-sm" data-testid="admin-create-result">
            <p>Owner login: <strong>{result.owner_email}</strong></p>
            <p>Temporary password: <code className="rounded bg-foreground/10 px-2 py-1" data-testid="admin-temp-password">{result.temporary_password}</code></p>
            <p className="text-muted-foreground">Share this securely. {f.send_invite ? "An invitation email with a set-password link was also sent." : ""}</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Company name</Label><Input value={f.company_name} onChange={set("company_name")} data-testid="admin-company-name-input" /></div>
            <div className="space-y-2"><Label>Owner name</Label><Input value={f.owner_name} onChange={set("owner_name")} data-testid="admin-owner-name-input" /></div>
            <div className="space-y-2"><Label>Owner email</Label><Input type="email" value={f.owner_email} onChange={set("owner_email")} data-testid="admin-owner-email-input" /></div>
            <div className="space-y-2"><Label>Password (optional)</Label><Input value={f.password} onChange={set("password")} placeholder="Auto-generate" data-testid="admin-password-input" /></div>
            <Pick label="Plan" value={f.plan} onChange={(v) => setF({ ...f, plan: v })} opts={PLANS} testId="admin-plan-select" />
            <Pick label="Access" value={f.payment_mode} onChange={(v) => setF({ ...f, payment_mode: v })} opts={[["complimentary", "Complimentary (no payment)"], ["razorpay", "Require Razorpay payment"]]} testId="admin-payment-mode-select" />
            {f.payment_mode === "complimentary" && <>
              <div className="space-y-2"><Label>Duration (months)</Label><Input type="number" min={1} value={f.months} onChange={set("months")} data-testid="admin-months-input" /></div>
              <div className="space-y-2"><Label>AI credits (blank = plan default)</Label><Input type="number" value={f.credits} onChange={set("credits")} data-testid="admin-credits-input" /></div>
            </>}
            <div className="space-y-2 sm:col-span-2"><Label>Notes</Label><Input value={f.notes} onChange={set("notes")} data-testid="admin-notes-input" /></div>
            <label className="sm:col-span-2 flex items-center gap-3 text-sm"><Switch checked={f.send_invite} onCheckedChange={(v) => setF({ ...f, send_invite: v })} data-testid="admin-invite-switch" /> Send invitation email</label>
          </div>
        )}
        {!result && <DialogFooter><button className="btn-brand" onClick={submit} data-testid="admin-create-submit">Create company</button></DialogFooter>}
      </DialogContent>
    </Dialog>
  );
};

const EditDialog = ({ c, setC, onSaved }) => {
  const [f, setF] = useState({ plan: "", add_credits: "", months: "" });
  const save = async (extra = {}) => {
    try {
      const body = { plan: f.plan || null, add_credits: f.add_credits ? Number(f.add_credits) : null, months: f.months ? Number(f.months) : null, ...extra };
      await api.patch(`/admin/companies/${c.id}`, body); toast.success("Updated"); onSaved(); setC(null); setF({ plan: "", add_credits: "", months: "" });
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!c} onOpenChange={(o) => !o && setC(null)}>
      <DialogContent>
        <DialogHeader><DialogTitle>Manage {c?.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Pick label="Grant plan (complimentary)" value={f.plan} onChange={(v) => setF({ ...f, plan: v })} opts={PLANS} testId="admin-edit-plan-select" />
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Add credits</Label><Input type="number" value={f.add_credits} onChange={(e) => setF({ ...f, add_credits: e.target.value })} data-testid="admin-edit-credits-input" /></div>
            <div className="space-y-2"><Label>Extend (months)</Label><Input type="number" value={f.months} onChange={(e) => setF({ ...f, months: e.target.value })} data-testid="admin-edit-months-input" /></div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <button className="btn-ghost !text-red-500" data-testid="admin-toggle-suspend" onClick={() => save({ status: c?.status === "suspended" ? "active" : "suspended" })}>{c?.status === "suspended" ? "Reactivate" : "Suspend"}</button>
          <button className="btn-brand" onClick={() => save()} data-testid="admin-edit-save">Save</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminCompanies() {
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(params.get("new") === "1");
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/companies").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const shown = items.filter((c) => `${c.name} ${c.owner_email}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="admin-companies-page">
      <PageTitle title="Companies & customers" copy="Add customers with full access without payment, or require Razorpay payment.">
        <button className="btn-brand" onClick={() => setOpen(true)} data-testid="admin-open-create">Add company</button>
      </PageTitle>
      <Input placeholder="Search companies or emails" value={q} onChange={(e) => setQ(e.target.value)} className="mb-4 max-w-sm" data-testid="admin-company-search" />
      <div className="card-box overflow-x-auto">
        <Table data-testid="admin-companies-table">
          <TableHeader><TableRow><TableHead>Company</TableHead><TableHead>Owner</TableHead><TableHead>Plan</TableHead><TableHead>Access</TableHead><TableHead>Credits</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>{shown.map((c) => (
            <TableRow key={c.id} data-testid={`admin-company-row-${c.id}`}>
              <TableCell className="font-medium">{c.name}<div className="text-xs text-muted-foreground">{c.user_count} user(s)</div></TableCell>
              <TableCell className="text-xs">{c.owner_email}</TableCell>
              <TableCell className="capitalize">{c.plan}{c.intended_plan && c.plan === "free" ? ` → ${c.intended_plan}` : ""}</TableCell>
              <TableCell className="text-xs">{c.payment_mode}</TableCell>
              <TableCell>{c.credits?.toLocaleString()}</TableCell>
              <TableCell><Badge s={c.status} /></TableCell>
              <TableCell><button className="text-xs font-semibold text-[#7c3aed]" onClick={() => setEdit(c)} data-testid={`admin-manage-${c.id}`}>Manage</button></TableCell>
            </TableRow>
          ))}</TableBody>
        </Table>
      </div>
      <CreateDialog open={open} setOpen={setOpen} onCreated={load} />
      <EditDialog c={edit} setC={setEdit} onSaved={load} />
    </div>
  );
}
